import React from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  EligibilitySnapshot,
} from '@features/patient-coverages';
import { EligibilityStatusIcon } from '@features/patient-coverages';
import {
  getAlertGuidance,
  STATUS_CONFIG,
} from '@features/patient-coverages/components/BillingTab/eligibilityStatusConfig';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { formatDistanceToNow, isPast, parseISO } from 'date-fns';

interface EligibilityInfoPanelProps {
  eligibility: EligibilitySnapshot;
  patientId?: string;
  encounterId?: string;
}


function formatTimestamp(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function isExpired(iso: string): boolean {
  try {
    return isPast(parseISO(iso));
  } catch {
    return false;
  }
}

export const EligibilityInfoPanel: React.FC<EligibilityInfoPanelProps> = ({
  eligibility,
  patientId,
  encounterId,
}) => {
  const navigate = useNavigate();
  const {
    status_light,
    summary,
    alerts,
    last_checked_at,
    expires_at,
  } = eligibility;

  const expired = isExpired(expires_at);
  const statusLabel = STATUS_CONFIG[status_light].label;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      {/* Header row: status dot + label + staleness */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EligibilityStatusIcon status={status_light} size={18} />
          <Typography variant="subtitle2">{statusLabel}</Typography>
        </Stack>

        <Tooltip title={`Expires: ${expires_at}`}>
          <Typography
            variant="caption"
            sx={{ color: expired ? 'error.main' : 'text.secondary' }}
          >
            {expired ? 'Expired' : `Expires ${formatTimestamp(expires_at)}`}
          </Typography>
        </Tooltip>
      </Stack>

      {/* Alerts — actionable guidance replaces generic summary */}
      {alerts.length > 0 ? (
        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
          {alerts.map(alert => (
            <Typography key={alert} variant="body2" color="text.secondary">
              {getAlertGuidance(alert)}
            </Typography>
          ))}
        </Stack>
      ) : (
        summary && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            {summary}
          </Typography>
        )
      )}

      {/* Last checked */}
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Last checked {formatTimestamp(last_checked_at)}
      </Typography>

      {/* Review button — show for non-GREEN status */}
      {status_light !== 'GREEN' && patientId && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              navigate(`/patient/${patientId}`, {
                state: { tab: 'billing', encounterId },
              })
            }
          >
            Review Eligibility Details
          </Button>
        </Box>
      )}
    </Box>
  );
};
